import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMutation, useQuery } from '@/apollo';
import { 
  BACKUP_DATABASE, 
  GET_BACKUP_PROGRESS,
  DOWNLOAD_BACKUP
} from '../../graphql/system-health';
import { X, Download, CheckCircle, AlertCircle, Loader2, Database } from 'lucide-react';

interface DatabaseBackupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type BackupStatus = 'STARTED' | 'DUMPING' | 'COMPRESSING' | 'COMPLETED' | 'FAILED';

interface BackupProgress {
  id: string;
  status: BackupStatus;
  progress: number;
  message: string;
  filePath?: string;
  fileSize?: number;
  createdAt: string;
}

const DatabaseBackupModal: React.FC<DatabaseBackupModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [backupId, setBackupId] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  const [backupDatabase, { loading: backupLoading }] = useMutation(BACKUP_DATABASE, {
    onCompleted: (data) => {
      if (data.backupDatabase.success && data.backupDatabase.backupId) {
        setBackupId(data.backupDatabase.backupId);
      }
    },
    onError: (error) => {
      console.error('Backup failed:', error);
    }
  });

  const { data: progressData, startPolling, stopPolling } = useQuery(GET_BACKUP_PROGRESS, {
    variables: { backupId: backupId || '' },
    skip: !backupId,
    pollInterval: 3000, // Poll every 3 seconds to avoid rate limiting
    fetchPolicy: 'network-only'
  });

  const progress: BackupProgress | null = progressData?.backupProgress || null;

  // Handle progress updates using useEffect instead of deprecated onCompleted
  React.useEffect(() => {
    if (progressData?.backupProgress) {
      const progress = progressData.backupProgress;
      if (progress.status === 'COMPLETED') {
        setIsComplete(true);
        stopPolling();
      } else if (progress.status === 'FAILED') {
        stopPolling();
      }
    }
  }, [progressData, stopPolling]);

  // Also check progress status directly in case onCompleted doesn't fire
  React.useEffect(() => {
    if (progress?.status === 'COMPLETED' && !isComplete) {
      setIsComplete(true);
      stopPolling();
    }
  }, [progress?.status, isComplete, stopPolling]);

  const handleStartBackup = async () => {
    try {
      const newBackupId = `backup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setBackupId(newBackupId);
      setIsComplete(false);
      
      const result = await backupDatabase({
        variables: { backupId: newBackupId }
      });
      
      if (result.data?.backupDatabase?.success) {
        startPolling(3000);
      } else {
        // Reset state if backup failed to start
        setBackupId(null);
        console.error('Backup failed to start:', result.data?.backupDatabase?.message);
      }
    } catch (error) {
      // Reset state if mutation failed
      setBackupId(null);
      console.error('Failed to start backup:', error);
    }
  };

  const [downloadBackup] = useMutation(DOWNLOAD_BACKUP);

  const handleDownload = async () => {
    if (!backupId) {
      console.error('No backup ID available for download');
      return;
    }

    try {
      // Get download URL from GraphQL
      const { data } = await downloadBackup({
        variables: { backupId }
      });

      if (!data?.downloadBackup) {
        console.error('No download URL received');
        alert('Download failed: No download URL received');
        return;
      }

      // Use the GraphQL-provided download URL
      const response = await fetch(data.downloadBackup, {
        method: 'GET',
        credentials: 'include', // Include cookies for authentication
        headers: {
          'Accept': 'application/zip',
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        alert(`Download failed: ${response.status} - ${errorText}`);
        return;
      }

      // Get the blob and create download link
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Try to get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `database_backup_${new Date().toISOString().split('T')[0]}.zip`;
      
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches) {
          filename = matches[1] || filename;
        }
      }
      
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Download error:', error);
      alert('Download failed. Please try again.');
    }
  };

  const handleClose = () => {
    stopPolling();
    setBackupId(null);
    setIsComplete(false);
    onClose();
  };

  const getStatusIcon = () => {
    if (!progress) return <Database className="w-6 h-6 text-blue-500" />;
    
    switch (progress.status) {
      case 'STARTED':
      case 'DUMPING':
      case 'COMPRESSING':
        return <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />;
      case 'COMPLETED':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'FAILED':
        return <AlertCircle className="w-6 h-6 text-red-500" />;
      default:
        return <Database className="w-6 h-6 text-blue-500" />;
    }
  };

  const getStatusMessage = () => {
    if (!progress) return 'Ready to start backup';
    return progress.message;
  };

  const getProgressPercentage = () => {
    return progress?.progress || 0;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              boxShadow: 'var(--shadow-xl)'
            }}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-border)' }}>
              <div className="flex items-center space-x-3">
                {getStatusIcon()}
                <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Database Backup
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="transition-colors"
                style={{ color: 'var(--color-text-muted)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--color-text-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--color-text-muted)';
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              {/* Status Message */}
              <div className="mb-4">
                <p className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>Status</p>
                <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{getStatusMessage()}</p>
              </div>

              {/* Progress Bar */}
              {progress && (
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Progress</span>
                    <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {getProgressPercentage()}%
                    </span>
                  </div>
                  <div 
                    className="w-full rounded-full h-2"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                  >
                    <motion.div
                      className="h-2 rounded-full"
                      style={{ backgroundColor: 'var(--color-accent)' }}
                      initial={{ width: 0 }}
                      animate={{ width: `${getProgressPercentage()}%` }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    />
                  </div>
                </div>
              )}

              {/* File Info */}
              {progress?.status === 'COMPLETED' && (
                <div 
                  className="mb-4 p-3 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-bg-success)',
                    borderColor: 'var(--color-success)'
                  }}
                >
                  <p className="text-sm" style={{ color: 'var(--color-success)' }}>
                    <strong>Backup completed successfully!</strong>
                  </p>
                  {progress?.fileSize && (
                    <p className="text-sm mt-1" style={{ color: 'var(--color-success)' }}>
                      File size: {formatFileSize(progress.fileSize)}
                    </p>
                  )}
                </div>
              )}

              {/* Error Message */}
              {progress?.status === 'FAILED' && (
                <div 
                  className="mb-4 p-3 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--color-bg-error)',
                    borderColor: 'var(--color-error)'
                  }}
                >
                  <p className="text-sm" style={{ color: 'var(--color-error)' }}>
                    <strong>Backup failed!</strong>
                  </p>
                  <p className="text-sm mt-1" style={{ color: 'var(--color-error)' }}>
                    {progress.message}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex justify-end space-x-3" style={{ borderColor: 'var(--color-border)' }}>
              {(!backupId || (!progress && !backupLoading)) && (
                <button
                  onClick={handleStartBackup}
                  className="px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2"
                  style={{ backgroundColor: 'var(--color-accent)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-accent-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-accent)';
                  }}
                  disabled={backupLoading}
                >
                  <Database className="w-4 h-4" />
                  <span>{backupLoading ? 'Starting...' : 'Start Backup'}</span>
                </button>
              )}

              {(isComplete || progress?.status === 'COMPLETED') && (
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 text-white rounded-lg transition-colors flex items-center space-x-2"
                  style={{ backgroundColor: 'var(--color-success)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-success-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-success)';
                  }}
                >
                  <Download className="w-4 h-4" />
                  <span>Download Backup</span>
                </button>
              )}

              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                {(isComplete || progress?.status === 'COMPLETED') ? 'Close' : 'Cancel'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default DatabaseBackupModal;
